/**
 * Horizontal ranked bars, 1 or 2 grouped series.
 * Geometry ported from blocks ④/⑤/⑥ of docs/plans/conversations/09-dashboard-mockup.html.
 *
 * - bars 14px thick (<= 24), data-end rounded 4px, square at the baseline (`hBarPath`)
 * - 2px gap in the surface colour between the two grouped bars of a row
 * - one axis, gridlines 1px solid recessive, value at the bar TIP
 * - a tip label that would overflow is MEASURED and moved inside the bar with
 *   `inkOn`-chosen ink — never clipped
 * - `value: null` renders « non mesuré »: a hole is not a zero
 * - sorting is the caller's job (data arrives ordered)
 * - a row may carry a logo (`iconSrc`) and/or be a link (`href`); both are pure
 *   geometry here — the caller owns which asset and which URL
 * - `iconOnly` replaces the text label with the mark for the rows that HAVE one
 *   (an engine logo, a flag). The name is never lost: it stays the first line of
 *   the row tooltip and the whole point of the `VizTable` twin. A row with no
 *   `iconSrc` keeps its text — an unknown engine must not render as a blank gutter.
 */

import { Box } from '@mui/material';
import { VIZ, fmtInt, fmtPct, hBarPath, inkOn, measureText, niceTicks, useMeasuredWidth } from './tokens';
import { EmptyNote, VizLegend } from './ChartCard';
import { useVizTooltip } from './useVizTooltip';

export interface RankedBarsRow {
  label: string;
  value: number | null;
  n?: number;
  tip?: string[];
  /** URL of a small square logo drawn to the left of the label (e.g. an engine mark). */
  iconSrc?: string;
  /** Makes the whole row a link. Opens in a new tab — the chart keeps its filters. */
  href?: string;
}

export interface RankedBarsProps {
  data: RankedBarsRow[];
  series2?: { label: string; value: number | null }[];
  seriesLabels?: [string, string];
  format?: (v: number) => string;
  maxValue?: number;
  emptyNote?: string;
  unmeasuredNote?: string;
  /** Draw the mark INSTEAD of the text label, wherever a row carries an `iconSrc`. */
  iconOnly?: boolean;
}

const BAR_H = 14;
const GAP_Y = 2; // 2px surface-colour gap between the two grouped bars
const ICON = 15; // logo box; +5px of air before the label
const ICON_W = ICON + 5;

export default function RankedBars({
  data,
  series2,
  seriesLabels,
  format = fmtPct,
  maxValue,
  emptyNote = 'Aucune donnée sur cette sélection.',
  unmeasuredNote = 'non mesuré',
  iconOnly = false,
}: RankedBarsProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(640);
  const tip = useVizTooltip();

  if (!data.length) return <EmptyNote>{emptyNote}</EmptyNote>;

  const grouped = !!series2;
  const rowH = grouped ? 2 * BAR_H + GAP_Y + 18 : 30;
  // One label gutter for the whole chart, widened once if ANY row has a logo: the
  // labels stay right-aligned on a single edge, so the logos line up too.
  const anyIcon = data.some((d) => !!d.iconSrc);
  /** The text this row actually renders — empty under `iconOnly` once it has a mark. */
  const textOf = (d: RankedBarsRow) => (iconOnly && d.iconSrc ? '' : d.label);
  const labelW = Math.min(
    240,
    // The floor drops with the text: an all-marks gutter needs room for the mark
    // and its 5px of air, not for a 70px label that is never drawn.
    Math.max(
      iconOnly ? 10 : 70,
      ...data.map((d) => (textOf(d) ? measureText(d.label, 12) + 14 : 0)),
    ) + (anyIcon ? ICON_W : 0),
  );
  const m = { l: labelW, r: 56, t: 6 };
  const W = Math.max(320, width);
  const H = data.length * rowH + 28;
  const pw = Math.max(40, W - m.l - m.r);

  const values = [
    ...data.map((d) => d.value),
    ...(series2 ?? []).map((d) => d.value),
  ].filter((v): v is number => v != null);
  // `maxValue` is a floor: it is rounded up to the next nice step so no gridline
  // ever lands outside the plot.
  const ticks = niceTicks(Math.max(maxValue ?? 0, 0.0001, ...values), 4);
  const max = ticks[ticks.length - 1] || 1;
  const X = (v: number) => m.l + (v / max) * pw;
  const plotBottom = m.t + data.length * rowH;

  const bar = (
    row: { label: string; value: number | null },
    y: number,
    color: string,
    slot: 0 | 1,
  ) => {
    const key = `${row.label}-${slot}`;
    if (row.value == null) {
      return (
        <text key={key} x={m.l + 4} y={y + BAR_H - 3} fill={VIZ.muted} fontSize={11} fontStyle="italic">
          {unmeasuredNote}
        </text>
      );
    }
    const w = (row.value / max) * pw;
    const text = format(row.value);
    const tw = measureText(text, 11);
    const outside = m.l + w + 7 + tw <= W - 4;
    return (
      <g key={key}>
        <path d={hBarPath(m.l, y, w, BAR_H, 4)} fill={color} />
        <text
          x={outside ? m.l + w + 7 : m.l + w - 7}
          y={y + BAR_H - 3}
          textAnchor={outside ? 'start' : 'end'}
          fill={outside ? VIZ.ink2 : inkOn(color)}
          fontSize={11}
          style={{ fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}
        >
          {text}
        </text>
      </g>
    );
  };

  // The gutter is capped, so a long label is cut with an ellipsis rather than
  // bleeding out of the card. The FULL label stays in the row tooltip.
  const textW = labelW - 14 - (anyIcon ? ICON_W : 0);
  const fitLabel = (s: string) => {
    if (measureText(s, 12) <= textW) return s;
    let cut = s;
    while (cut.length > 1 && measureText(cut + '…', 12) > textW) cut = cut.slice(0, -1);
    return cut + '…';
  };

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={
          grouped && seriesLabels
            ? `${seriesLabels[0]} et ${seriesLabels[1]}, barres classées`
            : 'Barres classées'
        }
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={X(t)} x2={X(t)} y1={m.t} y2={plotBottom} stroke={VIZ.grid} strokeWidth={1} />
            <text x={X(t)} y={H - 8} textAnchor="middle" fill={VIZ.muted} fontSize={11}>
              {format(t)}
            </text>
          </g>
        ))}
        <line x1={m.l} x2={m.l} y1={m.t} y2={plotBottom} stroke={VIZ.axis} strokeWidth={1} />

        {data.map((row, i) => {
          const top = m.t + i * rowH + (rowH - (grouped ? 2 * BAR_H + GAP_Y : BAR_H)) / 2;
          const s2 = series2?.[i];
          const lines = [
            row.label,
            ...(grouped && seriesLabels
              ? [
                  `${seriesLabels[0]} : ${row.value == null ? unmeasuredNote : format(row.value)}`,
                  `${seriesLabels[1]} : ${s2?.value == null ? unmeasuredNote : format(s2.value)}`,
                ]
              : [row.value == null ? unmeasuredNote : format(row.value)]),
            ...(row.tip ?? []),
          ];
          const labelY = grouped ? top + BAR_H + 1 : top + BAR_H - 2;
          const shown = textOf(row) ? fitLabel(row.label) : '';
          const body = (
            <>
              {row.iconSrc && (
                <image
                  href={row.iconSrc}
                  // With no text beside it the mark takes the text's own right margin,
                  // so an all-marks gutter has no dead air before the axis.
                  x={m.l - 10 - measureText(shown, 12) - (shown ? ICON_W : ICON)}
                  y={labelY - ICON + 3}
                  width={ICON}
                  height={ICON}
                  preserveAspectRatio="xMidYMid meet"
                  // The accessible name, since the text label is gone. NOT a <title>:
                  // that would stack a native tooltip under the chart's own one.
                  role="img"
                  aria-label={row.label}
                />
              )}
              <text
                x={m.l - 10}
                y={labelY}
                textAnchor="end"
                fill={VIZ.ink2}
                fontSize={12}
                style={row.href ? { textDecoration: 'underline', textDecorationColor: VIZ.axis } : undefined}
              >
                {shown}
              </text>
              {row.n != null && !grouped && (
                <text
                  x={m.l - 10}
                  y={top + BAR_H + 10}
                  textAnchor="end"
                  fill={VIZ.muted}
                  fontSize={10.5}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtInt(row.n)}
                </text>
              )}
              {bar(row, top, VIZ.series[0], 0)}
              {s2 && bar(s2, top + BAR_H + GAP_Y, VIZ.series[1], 1)}
              {/* hit target larger than the marks */}
              <rect
                x={0}
                y={m.t + i * rowH}
                width={W}
                height={rowH}
                fill="transparent"
                style={row.href ? { cursor: 'pointer' } : undefined}
                onPointerMove={(ev) => tip.show(ev, lines)}
                onPointerLeave={tip.hide}
              />
            </>
          );
          return row.href ? (
            <a key={row.label} href={row.href} target="_blank" rel="noreferrer">
              {body}
            </a>
          ) : (
            <g key={row.label}>{body}</g>
          );
        })}
      </svg>
      {grouped && seriesLabels && (
        <VizLegend
          items={[
            { label: seriesLabels[0], color: VIZ.series[0] },
            { label: seriesLabels[1], color: VIZ.series[1] },
          ]}
        />
      )}
      {tip.node}
    </Box>
  );
}
